import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Loader2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import MarketingHeader from "../components/argus/MarketingHeader";
import Footer from "../components/argus/Footer";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { api } from "../lib/api";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const navigate = useNavigate();
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;
    if (pw.length < 8) {
      toast.error("Password must be at least 8 characters.");
      return;
    }
    if (pw !== pw2) {
      toast.error("Passwords don't match.");
      return;
    }
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { token, password: pw });
      setDone(true);
      toast.success("Password updated. Sign in below.");
      setTimeout(() => navigate("/login"), 1800);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't reset password.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="bg-paper text-ink min-h-screen flex flex-col">
      <MarketingHeader />
      <section className="flex-1 mx-auto max-w-md w-full px-4 py-16">
        {!token ? (
          <div className="rounded-lg border border-line bg-panel p-5" data-testid="reset-missing-token">
            <h1 className="font-display text-3xl mb-2">Missing reset token.</h1>
            <p className="text-ink/70 mb-4">Open the reset link from your email inbox.</p>
            <Link to="/forgot-password" className="text-gold underline">Request a new link</Link>
          </div>
        ) : done ? (
          <div className="text-center" data-testid="reset-done">
            <CheckCircle2 size={48} className="mx-auto text-safe mb-4" />
            <h1 className="font-display text-3xl">Password updated.</h1>
            <p className="text-ink/70 mt-2">Redirecting to login…</p>
          </div>
        ) : (
          <>
            <h1 className="font-display text-4xl mb-2" data-testid="reset-title">Choose a new password.</h1>
            <p className="text-ink/70 mb-8">Eight characters minimum. Make it good.</p>
            <form onSubmit={submit} className="space-y-4" data-testid="reset-form">
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-wider text-ink/60">New password</Label>
                <Input
                  type="password"
                  value={pw}
                  onChange={(e) => setPw(e.target.value)}
                  required
                  autoComplete="new-password"
                  className="bg-paper border-line text-ink h-12"
                  data-testid="reset-pw1"
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-wider text-ink/60">Confirm</Label>
                <Input
                  type="password"
                  value={pw2}
                  onChange={(e) => setPw2(e.target.value)}
                  required
                  autoComplete="new-password"
                  className="bg-paper border-line text-ink h-12"
                  data-testid="reset-pw2"
                />
              </div>
              <Button
                type="submit"
                disabled={busy}
                className="w-full bg-gold text-paper hover:bg-gold2 h-12 font-semibold"
                data-testid="reset-submit"
              >
                {busy ? <><Loader2 size={16} className="mr-2 animate-spin" /> Updating…</> : "Update password"}
              </Button>
            </form>
          </>
        )}
      </section>
      <Footer />
    </div>
  );
}
